import { isApiError } from '@npc/api-client'

import { API_BASE } from './api'
import { render } from '../copy'

/**
 * 请求失败分三种，**因为修法完全不同**。
 *
 * 上线那天最容易撞的就是这一层，而前几轮反复栽的都是同一件事：
 * 一句笼统的报错把人指向错误的方向。
 *
 * | kind | 实际发生了什么 | 谁该动手，怎么修 |
 * |---|---|---|
 * | `unreachable` | 请求压根没到后端 | **部署的人**：地址配错、反代没起来、CORS 挡了 |
 * | `missing` | 到了后端，**后端没有这条路由** | **后端**：这一块还没挂上。用户做什么都没用 |
 * | `unauthorized` | 到了后端，后端说你没登录 | **用户**：重新登一下 |
 * | `needs_passkey` | 到了后端，后端要二次验证 | **用户**：当场再验一次（邮箱验证码或登录密码，见 StepUpDialog）——**不是权限不够**。
    名字里的 passkey 是历史：Passkey 2026-08-25 退役时这个 kind 已在错误处理里铺开，改名的动静比留下的误导大 |
 * | `reported` | 到了后端，后端说了别的 | 看后端那句话 |
 *
 * **它们在界面上长得很像，而中间隔着整个网络层和整个后端。**
 * 混成一句「出错了」，人会去查登录，而毛病在 nginx 上。
 *
 * ## `missing` 为什么必须单独一档
 *
 * 线上实测撞出来的：`/alerts` `/incidents` `/notification-rules` 三条契约里
 * 有、后端没挂，于是 `alert` 屏**点进去是坏的，不是空的**。
 *
 * 而「这一块还没上」与「暂时没有数据」是两件完全相反的事：
 *
 * - 没有数据 = **正常状态**，而且往往是好消息（「沉默是好消息」）
 * - 接口不存在 = 用户**做什么都没用**，等也没用，刷新也没用
 *
 * 更要命的是默认的兜底会说「我再试试」——**那是一个我们兑现不了的承诺**：
 * 一条不存在的路由，重试一万次也还是不存在。
 *
 * arron 是唯一目标用户，他点进一个坏掉的页面时不会想到「这是还没做」，
 * 他会以为平台坏了。
 *
 * **怎么分清「路由不存在」和「这个东西没了」：** 两者都是 404，
 * 但后者是后端**按契约**回的（有 `Error` 形状、有 `code`、有 `user_message`），
 * 前者是路由表没匹配上，回的是框架的默认 404（不是 JSON）。
 * 所以判据还是 content-type——**不是状态码**。
 */
export type ApiFailureKind = 'unreachable' | 'missing' | 'unauthorized' | 'needs_passkey' | 'reported'

export class ApiFailure extends Error {
  readonly kind: ApiFailureKind
  readonly status?: number
  readonly code?: string
  /**
   * 后端按契约放在 `Error.details` 里的那一份，**原样带上来，不解释**。
   *
   * 类型是 `unknown` 而不是某个具体形状，这是刻意的：契约里 `details`
   * 就是 `{ [key: string]: unknown }`，谁要用谁在自己那侧校验。
   * 在这里给它一个类型，等于替所有端点声明它们的 details 长什么样。
   *
   * 加它是因为有些 409 把「用户接下来要看的东西」放在这里——比如停用
   * Agent 时那四档影响面。没有它，界面只能说一句「有东西挡着」，
   * 而用户要知道挡着的是什么才决定要不要继续。
   */
  readonly details?: unknown

  constructor(
    kind: ApiFailureKind,
    message: string,
    metadata: { status?: number; code?: string; details?: unknown } = {},
  ) {
    super(message)
    this.name = 'ApiFailure'
    this.kind = kind
    this.status = metadata.status
    this.code = metadata.code
    this.details = metadata.details
  }
}

/**
 * 把一次 API 调用的三种结局收敛成 `ApiFailure`。
 *
 * ## 为什么要包一层 try
 *
 * `openapi-fetch` 只在**拿到了响应**时把错误放进 `result.error`。
 * 网络这一层断掉的时候（DNS 不通、连接被拒、CORS 被挡、混合内容被浏览器拦），
 * `fetch` 是**直接抛**的——那个异常会一路冒到 TanStack Query，
 * `error.message` 变成浏览器给的英文串（`Failed to fetch` 之类），
 * 然后**原样渲染到用户脸上**。
 *
 * 那句话既不是模板，也不是人话，还正好是最需要说清楚的那一种失败。
 *
 * ## 用户看模板，部署的人看控制台
 *
 * 用户可见的那句一律来自 `docs/copy/`。而「API 地址是什么、失败在哪一层」
 * 这些是**给部署的人看的**，走 `console.error`——
 * 它们不是文案，写进模板反而会把术语推到第一屏。
 */
export async function call<T>(
  label: string,
  run: () => Promise<{ data?: T; error?: unknown; response?: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response?: Response }

  try {
    result = await run()
  } catch (cause) {
    // 走到这儿 = 请求没有拿到任何响应
    console.error(
      `[npc] 连不上后端：${label}\n` +
        `  API 地址 = ${API_BASE}\n` +
        `  这一层的失败与登录无关——请求没有拿到任何响应。\n` +
        `  常见原因：地址配错（VITE_API_BASE）、反代没起来、跨域被挡、http 页面里请求 https。`,
      cause,
    )
    throw new ApiFailure('unreachable', render('error.generic'))
  }

  if (result.error === undefined && result.data !== undefined) return result.data
  if (result.error === undefined && result.response?.status === 204) return undefined as T

  const status = result.response?.status
  const contentType = result.response?.headers.get('content-type') ?? ''

  /**
   * **答话的不是我们的 API。**
   *
   * 这一档是实测加出来的：dev 下把后端停掉，Vite 代理把 ECONNREFUSED 变成
   * 一个 500，于是从浏览器看「有响应」，第一版就报成了「后端回了一个认不出
   * 形状的东西」——**它把人指向后端的返回值，而真相是根本没有后端在听。**
   *
   * 生产上是同一个形状，只是换了个门房：nginx 给 502/503/504，
   * CDN 给一个 HTML 错误页。判据用 content-type 而不是状态码，
   * 因为最坑的那种misconfig 会返回 **200**：
   *
   * > SPA 的 fallback 规则把 `/api/v1/*` 也吃了，于是每个 API 请求都拿到
   * > `index.html`，状态码 200。不看 content-type 的话，这会表现为
   * > 「后端返回的东西解析不了」——而毛病在一行 nginx 配置上。
   */
  const answeredByOurApi = contentType.includes('json')

  if (!answeredByOurApi && status === 404) {
    console.error(
      `[npc] 后端没有这条路由：${label}\n` +
        `  API 地址 = ${API_BASE}，HTTP 404，content-type = ${contentType || '（没有）'}\n` +
        `  这是**路由表没匹配上**，不是「这个东西没了」——后者后端会按契约回一个\n` +
        `  带 code 与 user_message 的 JSON。也就是说这一块后端还没挂上。\n` +
        `  界面这边按「还没上」处理：不承诺重试，也不给行动点——用户做什么都没用。`,
    )
    throw new ApiFailure('missing', render('error.generic'))
  }

  if (!answeredByOurApi) {
    console.error(
      `[npc] 答话的不是后端：${label}\n` +
        `  API 地址 = ${API_BASE}，HTTP ${status ?? '?'}，content-type = ${contentType || '（没有）'}\n` +
        `  请求确实拿到了响应，但那不是我们的 API 回的——**先别去查登录，也别去查后端的代码**。\n` +
        `  三种最常见的：\n` +
        `    1. 反代/网关答的（502 / 503 / 504）：后端没起来，或者 upstream 地址写错\n` +
        `    2. 拿到了 index.html（多半是 200）：SPA 的 fallback 把 /api/v1/* 也吃了\n` +
        `    3. API 地址指错了地方（VITE_API_BASE）`,
    )
    throw new ApiFailure('unreachable', render('error.generic'))
  }

  if (status === 401) {
    /**
     * **401 还有第三副面孔：他刚输的那个不对。**
     *
     * 「当场再验一次」的密码/验证码输错，后端按契约回的也是 401（见
     * `WRONG_SECRET_CODES` 那份名单）。此时**会话好好的**，错的是刚敲
     * 进去的哪一个——下一步是原地重输，不是重新登录。而跳登录页由
     * `kind` 决定（query-client），这些码若也归 `unauthorized`，一个
     * 正在邀请成员的已登录用户会被整页踢去 /auth，填了一半的表全丢——
     * 2026-09-11 生产上报的就是这一条。
     *
     * 判据是 code（契约），不是状态码，与 `step_up_required` 那条同理。
     * 名单之外的一切 401（含没有 Error 形状的）仍按会话过期处理：
     * **宁可多跳一次登录页，不可把「没登录」悄悄放过。**
     */
    if (isApiError(result.error) && WRONG_SECRET_CODES.has(result.error.code)) {
      throw new ApiFailure('reported', result.error.user_message, {
        status,
        code: result.error.code,
      })
    }
    console.error(`[npc] 后端说你没登录：${label}（HTTP 401，API 地址 = ${API_BASE}）`)
    /**
     * **401 不总是「你的会话过期了」。**
     *
     * 登录本身失败也是 401——码输错了、恢复码不对。对着一个正在登录的人说
     * 「你这次的登录已经过期了」是句没头没脑的话：他压根还没登进来过。
     *
     * 所以后端按契约给了句子就用它的（那句是它按模板渲染的，比这边猜得准），
     * 只有 401 光秃秃什么都没带时才退到「会话过期」那条。
     *
     * `kind` 仍然是 `unauthorized`——**跳登录页那个行为由 kind 决定，
     * 与显示哪句话是两件事**。已经在 `/auth` 上的不会被再跳一次（见 query-client）。
     */
    throw new ApiFailure(
      'unauthorized',
      isApiError(result.error) ? result.error.user_message : render('error.unauthorized'),
      { status, ...(isApiError(result.error) ? { code: result.error.code } : {}) },
    )
  }

  // 后端按契约给了 Error 形状：**照原样用它的 user_message**。
  // 那句话是后端用 docs/copy 的模板渲染好的，前端再包装一次就是两份真相
  if (isApiError(result.error)) {
    /**
     * **「要二次验证」不是「权限不够」，这两件事的下一步完全相反。**
     *
     * 线上实测：没带一次性令牌时 `DELETE /hosts/{id}` 回 403 `step_up_required`。
     * 而 403 这个码本身把人推向「我权限不够，去找管理员」——
     * 他实际要做的是**当场再验一次**（邮箱验证码或登录密码，
     * StepUpDialog 就是干这个的），一件他自己就能完成的事。
     *
     * 判据是 `code` 不是状态码，也不是那句话的文字：
     * 状态码两类共用，文案会改，**`code` 是契约**。
     */
    if (result.error.code === 'step_up_required') {
      throw new ApiFailure('needs_passkey', result.error.user_message, { status, code: result.error.code })
    }
    throw new ApiFailure('reported', result.error.user_message, {
      status,
      code: result.error.code,
      // 契约里的 details 原样带上去。用它的那一侧自己校验形状。
      details: (result.error as { details?: unknown }).details,
    })
  }

  console.error(
    `[npc] 后端回了一个认不出形状的东西：${label}（HTTP ${status ?? '?'}，API 地址 = ${API_BASE}）`,
    result.error,
  )
  throw new ApiFailure('reported', render('error.generic'))
}

/**
 * 这些 401 说的是「**你刚输的那个**不对」，不是「你没登录」。
 *
 * 名单来自后端 auth 包的错误定义（`services/control/internal/auth/errors.go`）。
 * code 是契约：后端加新码要两边一起动，见下面 401 那一段。
 */
const WRONG_SECRET_CODES: ReadonlySet<string> = new Set([
  'auth.password_invalid', // 登录/二次验证：密码不对
  'auth.code_wrong', // 登录/二次验证：验证码不对
  'auth.code_expired', // 验证码过期
  'auth.registration_invalid', // 注册/引导 token 无效
])

export function isForbiddenError(error: unknown): boolean {
  return error instanceof ApiFailure && error.status === 403 && error.code !== 'step_up_required'
}
